import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, X, Minus } from "lucide-react";
import { TOUR_STEPS, GUIDE } from "@/data/portfolio";
import GuideIcon from "@/components/site/GuideIcon";
import { track } from "@/lib/analytics";

export default function GuidedTour({ step, onStep, onExit, onAsk }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeEl = useRef(null);
  const current = TOUR_STEPS[step];

  useEffect(() => {
    if (pathname !== "/") navigate("/");
  }, [pathname, navigate]);

  useEffect(() => {
    let tries = 0;
    let timer;
    const clear = () => activeEl.current?.classList.remove("tour-spotlight");
    const focus = () => {
      const el = document.getElementById(current.id);
      if (!el) {
        if (tries++ < 20) timer = setTimeout(focus, 100);
        return;
      }
      clear();
      el.classList.add("tour-spotlight");
      activeEl.current = el;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      track("tour_step", { step: current.id });
    };
    focus();
    return () => {
      clearTimeout(timer);
      clear();
    };
  }, [current]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onExit();
      if (e.key === "ArrowRight" && step < TOUR_STEPS.length - 1) onStep(step + 1);
      if (e.key === "ArrowLeft" && step > 0) onStep(step - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, onStep, onExit]);

  const last = step === TOUR_STEPS.length - 1;
  const { t } = useLang();
  const [min, setMin] = useState(false);

  if (min) {
    return (
      <div className="fixed inset-x-0 bottom-3 z-[60] flex justify-center px-3 pointer-events-none">
        <div data-testid="tour-mini" className="pointer-events-auto flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-[#14352a]/95 text-[#faf8f5] shadow-xl border border-[#2e333c]">
          <span className="h-7 w-7 rounded-full btn-guide grid place-items-center"><GuideIcon className="h-4 w-4" /></span>
          <span className="font-mono-label text-[9px] text-[#d9a92e]">{step + 1} / {TOUR_STEPS.length}</span>
          <span className="text-xs max-w-[40vw] truncate">{t(current.title)}</span>
          <button data-testid="tour-expand-btn" onClick={() => setMin(false)} className="h-7 px-2 rounded-full border border-white/20 text-[10px]">{t("Show")}</button>
          <button data-testid="tour-next-mini" onClick={() => (last ? onExit(true) : onStep(step + 1))} className="h-7 w-7 grid place-items-center rounded-full btn-guide" aria-label="Next"><ArrowRight className="h-3.5 w-3.5" /></button>
          <button data-testid="tour-exit-mini" onClick={onExit} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/10" aria-label="Exit tour"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 sm:bottom-4 z-[60] flex justify-center sm:px-3 pointer-events-none">
    <div
      data-testid="tour-bar"
      className="pointer-events-auto w-full sm:w-[min(94vw,640px)] max-h-[55vh] overflow-y-auto bg-[#14352a]/95 backdrop-blur-md text-[#faf8f5] border border-[#2e333c] shadow-2xl rounded-t-2xl sm:rounded-2xl p-4 sm:p-5 fade-up"
    >
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 shrink-0 rounded-full btn-guide grid place-items-center">
          <GuideIcon className="h-6 w-6" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 font-mono-label text-[9px] text-[#d9a92e]">
            {GUIDE.name} · {t("step")} {step + 1} / {TOUR_STEPS.length}
          </div>
          <div data-testid="tour-title" className="mt-1 font-display text-lg leading-tight">
            {t(current.title)}
          </div>
          <p data-testid="tour-text" className="mt-1.5 text-sm text-[#faf8f5]/80 leading-relaxed">
            {t(current.text)}
          </p>
        </div>
        <button
          data-testid="tour-minimize-btn"
          onClick={() => setMin(true)}
          className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10"
          aria-label="Minimize tour"
        >
          <Minus className="h-4 w-4" />
        </button>
        <button
          data-testid="tour-exit-btn"
          onClick={onExit}
          className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10"
          aria-label="Exit tour"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {TOUR_STEPS.map((s, i) => (
            <button
              key={s.id}
              data-testid={`tour-dot-${i}`}
              onClick={() => onStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-[#d9a92e]" : "w-1.5 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="tour-ask-btn"
            onClick={() => onAsk(current)}
            className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-full border border-white/20 text-xs hover:bg-white/10"
          >
            <MessageCircle className="h-3.5 w-3.5" /> {t("Ask about this")}
          </button>
          <button
            data-testid="tour-back-btn"
            onClick={() => onStep(step - 1)}
            disabled={step === 0}
            className="h-9 w-9 grid place-items-center rounded-full border border-white/20 disabled:opacity-30 hover:bg-white/10"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="tour-next-btn"
            onClick={() => (last ? onExit(true) : onStep(step + 1))}
            className="h-9 px-4 rounded-full btn-guide text-sm font-medium inline-flex items-center gap-1.5"
          >
            {last ? t("Finish") : t("Next")}
            {!last && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
    </div>
  );
}
